// src/components/Header.js
// Floating pill "dock" navigation — a centered, detached rounded island that
// condenses on scroll. Bold & energetic theme, logo orange (#F29200).
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_URL =
  'https://ehsfztlgxjkdeuobjttw.supabase.co/storage/v1/object/public/others/zabra%20logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Keep the HTTPS redirect behaviour that used to live in the old header.
  useEffect(() => {
    if (window.location.protocol === 'http:') {
      if (typeof navigator.share === 'undefined' || typeof navigator.canShare === 'undefined') {
        window.location.replace(window.location.href.replace(/^http:/, 'https:'));
      }
    }
  }, []);

  // Condense the dock once the user scrolls past the hero fold.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === null) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const section = document.getElementById(sectionId);
    if (section) {
      const y = section.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleSmoothScroll = (event, sectionId) => {
    event.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 120);
    } else {
      scrollToSection(sectionId);
    }
  };

  const handleTopScroll = (event) => {
    event.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 120);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Floating dock — fixed, centered, detached from the top edge */}
      <div className="fixed inset-x-0 top-4 md:top-6 z-50 flex justify-center px-4 pointer-events-none">
        <nav
          className={`z-dock pointer-events-auto flex items-center gap-2 rounded-full border-2 border-[var(--z-ink)] bg-white/95 backdrop-blur-md ${
            scrolled
              ? 'py-1.5 pl-2 pr-2 shadow-[0_6px_0_0_var(--z-ink)]'
              : 'py-2 pl-3 pr-2 shadow-[0_8px_0_0_var(--z-ink)]'
          }`}
        >
          <a href="/" onClick={handleTopScroll} className="flex items-center pr-1">
            <img
              src={LOGO_URL}
              alt="Zabra Solutions"
              className={`transition-all duration-300 ${scrolled ? 'h-8 md:h-9' : 'h-9 md:h-11'}`}
            />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            <a href="/" onClick={handleTopScroll} className={`z-dock-link ${location.pathname === '/' ? 'is-active' : ''}`}>
              Laman Utama
            </a>
            <a href="/#kami-tawarkan" onClick={(e) => handleSmoothScroll(e, 'kami-tawarkan')} className="z-dock-link">
              Kami Tawarkan
            </a>
            <a href="/#services" onClick={(e) => handleSmoothScroll(e, 'services')} className="z-dock-link">
              Perkhidmatan
            </a>
            <Link to="/contact" className="btn-accent ml-1 !py-2 !px-5 text-sm">
              Hubungi Kami
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMenuOpen(true)}
            aria-label="Buka menu"
            className="md:hidden ml-1 grid place-items-center h-10 w-10 rounded-full bg-[var(--z-accent)] text-white shadow-[0_4px_0_0_var(--z-accent-700)] active:translate-y-0.5 transition"
          >
            <Menu size={22} />
          </button>
        </nav>
      </div>

      {/* Fullscreen mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-[var(--z-ink)]/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
        <div
          className={`absolute inset-x-4 top-4 rounded-3xl border-2 border-[var(--z-ink)] bg-white p-6 shadow-[0_10px_0_0_var(--z-accent)] transition-transform duration-300 ${
            isMenuOpen ? 'translate-y-0' : '-translate-y-8'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <img src={LOGO_URL} alt="Zabra Solutions" className="h-9" />
            <button
              onClick={() => setIsMenuOpen(false)}
              aria-label="Tutup menu"
              className="grid place-items-center h-10 w-10 rounded-full border-2 border-[var(--z-ink)]"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            <MobileLink onClick={(e) => handleTopScroll(e)}>Laman Utama</MobileLink>
            <MobileLink onClick={(e) => handleSmoothScroll(e, 'kami-tawarkan')}>Kami Tawarkan</MobileLink>
            <MobileLink onClick={(e) => handleSmoothScroll(e, 'services')}>Perkhidmatan</MobileLink>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="btn-accent w-full mt-3 text-lg">
              Hubungi Kami
            </Link>
          </nav>
        </div>
      </div>
    </>
  );
};

const MobileLink = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="text-left font-serif-display text-2xl font-semibold py-3 px-2 rounded-xl text-[var(--z-ink)] hover:bg-[var(--z-accent-50)] hover:text-[var(--z-accent-700)] transition"
  >
    {children}
  </button>
);

export default Header;
